/**
 * After a new deploy, an already-open tab still points at the old build's file names, which no longer
 * exist. When loading a screen fails that way, reload once to pick up the new build.
 */
const KEY = 'lumnos.chunkReloadAt';
export const isChunkError = (e: unknown) => /dynamically imported module|Importing a module script failed|error loading dynamically imported|Failed to fetch dynamically|ChunkLoadError|Unable to preload CSS/i.test(String((e as Error)?.message ?? e));

export function reloadForNewVersion(): boolean {
  let last = 0;
  try { last = Number(sessionStorage.getItem(KEY) ?? 0); } catch { /* storage blocked */ }
  if (Date.now() - last < 30_000) return false; // already tried; don't loop
  try { sessionStorage.setItem(KEY, String(Date.now())); } catch { /* ignore */ }
  window.location.reload();
  return true;
}

/** Wraps a dynamic import: on a stale-build failure, reload the page instead of showing an error. */
export function withReload<T>(load: () => Promise<T>): () => Promise<T> {
  // Flaky networks (school filters, proxies) drop requests now and then: retry a few times before
  // assuming a new deploy and reloading.
  return async () => {
    let last: unknown;
    for (let i = 0; i < 3; i++) {
      try { return await load(); } catch (e) {
        last = e;
        if (!isChunkError(e)) throw e;
        await new Promise((r) => setTimeout(r, 600 * (i + 1)));
      }
    }
    if (reloadForNewVersion()) return new Promise<T>(() => {});
    throw last;
  };
}

if (typeof window !== 'undefined') {
  // Vite reports failed preloads here too.
  window.addEventListener('vite:preloadError', (e) => { if (reloadForNewVersion()) e.preventDefault(); });
}
